import { IconMinus, IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import { createContext, useContext, useState } from "react";

const CaseToggleContext = createContext<{ toggle: () => void; isOpen: boolean } | undefined>(undefined);

const useCaseToggle = () => {
	const context = useContext(CaseToggleContext);
	if (!context) {
		throw new Error("useCaseToggle must be used within a CaseBlock");
	}
	return context;
};

export const CaseLayout = ({
	children,
	className,
}: {
	children?: React.ReactNode;
	className?: string;
}) => {
	return (
		<div className={clsx("flex flex-col w-full pl-3 mt-6", className)}>
			<div className="flex flex-col w-full gap-6 pl-8 border-l border-border/50">{children}</div>
		</div>
	);
};

export const CaseBlock = ({
	children,
	showContent = true,
}: {
	children?: React.ReactNode;
	showContent?: boolean;
}) => {
	const [isOpen, setIsOpen] = useState(showContent);
	const toggle = () => setIsOpen((prev) => !prev);

	return (
		<CaseToggleContext.Provider value={{ isOpen, toggle }}>
			<div className="flex flex-col w-full gap-2">{children}</div>
		</CaseToggleContext.Provider>
	);
};

export const CaseBlockHead = ({
	children,
	customIcon,
	inlineIcon = false,
	className,
}: {
	children?: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
	customIcon?: (isOpen: boolean) => React.ReactNode;
	inlineIcon?: boolean;
	className?: string;
}) => {
	const { toggle, isOpen } = useCaseToggle();

	const content = typeof children === "function" ? children(isOpen) : children;

	return (
		<div className={clsx("inline-flex flex-row items-center w-full", inlineIcon ? "items-center gap-2" : "relative", className)}>
			<button
				type="button"
				onClick={toggle}
				className={clsx(
					"inline-flex justify-center items-center w-[24px] h-[24px] rounded-full bg-background border border-border text-foreground/50 hover:text-foreground",
					!inlineIcon && "absolute top-0 right-full mr-5",
				)}
			>
				{customIcon ? customIcon(isOpen) : isOpen ? <IconMinus size={14} stroke={2} /> : <IconPlus size={14} stroke={2} />}
			</button>
			{content}
		</div>
	);
};

export const CaseBlockBody = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
	const { isOpen } = useCaseToggle();
	return isOpen ? <div className={clsx("flex flex-col w-full rounded-md overflow-hidden", className)}>{children}</div> : null;
};
